import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import apiClient, { getAuthHeaders, getStoredToken, withApiPath } from '../utils/apiClient';

export const LoadContext = createContext();
export const AUTH_SCOPE_CHANGED_EVENT = 'freightlogic:auth-scope-changed';

const EMPTY_FTL_STATS = {
  loads_this_week: null,
  loads_previous_week: null,
  loads_week_change_percentage: null,
  bookings_confirmed: null,
  bookings_total: null,
  loads_with_issues: null,
  on_time_delivery_percentage: null,
  on_time_delivery_sample_size: null,
};

const FILTER_QUERY_MAP = {
  "Today's Loads - Picking Up": 'today_pickup',
  "Today's Loads - Delivering": 'today_delivery',
  "Tomorrow's Loads - Picking Up": 'tomorrow_pickup',
  "Tomorrow's Loads - Delivering": 'tomorrow_delivery',
  'Delivered Loads': 'delivered',
  'Over the Road': 'otr',
  "Yesterday's Loads - Delivering": 'yesterday_delivery',
  'Ready for Bill Loads': 'ready_to_bill',
  'Completed Loads': 'complete',
};

export const LoadProvider = ({ children }) => {
  const [loads, setLoads] = useState([]);
  const [ftlStats, setFtlStats] = useState(EMPTY_FTL_STATS);
  const costPerMileData = [];
  const timeToAcceptanceData = [];
  const [statsStatus, setStatsStatus] = useState('idle');
  const hasStats = useRef(false);
  const statsAccountId = useRef(null);

  const getStoredAccountId = useCallback(() => {
    if (typeof window === 'undefined') {
      return null;
    }
    return localStorage.getItem('accountId') || sessionStorage.getItem('accountId');
  }, []);

  const fetchFTLLoadboardData = useCallback(async (accountId, filterOption = '', ordering = '') => {
    const storedToken = getStoredToken();
    if (!storedToken || !accountId) {
      return;
    }

    const params = {
      shipment_type: 'FTL',
      account: accountId,
    };

    if (filterOption) {
      const mappedFilter = FILTER_QUERY_MAP[filterOption] || filterOption;
      params.operational_filter = mappedFilter;
    }
    if (ordering) params.ordering = ordering;

    try {
      const response = await apiClient.get(withApiPath('/loads/'), {
        headers: getAuthHeaders(storedToken),
        params,
      });
      const data = response.data;
      const payload = Array.isArray(data) ? data : data.results || [];
      setLoads(payload);
    } catch (error) {
      console.error('Error fetching FTL loadboard data:', error);
      setLoads([]);
    }
  }, []);

  const fetchFTLStats = useCallback(async (accountId) => {
    const storedToken = getStoredToken();
    if (!storedToken || !accountId) return;

    const isNewScope = statsAccountId.current !== accountId;
    if (!hasStats.current || isNewScope) {
      hasStats.current = false;
      statsAccountId.current = accountId;
      setFtlStats(EMPTY_FTL_STATS);
      setStatsStatus('loading');
    }
    try {
      const response = await apiClient.get(withApiPath('/basicstatsftl/stats/'), {
        headers: getAuthHeaders(storedToken),
      });
      if (statsAccountId.current !== accountId) return;
      setFtlStats(response.data);
      hasStats.current = true;
      statsAccountId.current = accountId;
      setStatsStatus('success');
    } catch (error) {
      console.error('Error fetching FTL overview statistics:', error);
      if (!hasStats.current && statsAccountId.current === accountId) {
        setFtlStats(EMPTY_FTL_STATS);
        setStatsStatus('error');
      }
    }
  }, []);

  const refreshFTLData = useCallback((accountId, filterOption = '', ordering = '') => (
    Promise.all([
      fetchFTLLoadboardData(accountId, filterOption, ordering),
      fetchFTLStats(accountId),
    ])
  ), [fetchFTLLoadboardData, fetchFTLStats]);

  const addLoad = useCallback(
    async (loadData) => {
      const storedToken = getStoredToken();
      const accountId = getStoredAccountId();

      if (!storedToken || !accountId) {
        throw new Error('Authentication details are missing. Please sign in again.');
      }

      const payload = {
        ...loadData,
        account: accountId,
      };

      const response = await apiClient.post(withApiPath('/loads/'), payload, {
        headers: getAuthHeaders(storedToken),
      });

      await refreshFTLData(accountId);

      return response.data;
    },
    [getStoredAccountId, refreshFTLData]
  );

  const [authScopeVersion, setAuthScopeVersion] = useState(0);
  useEffect(() => {
    const handleScopeChange = () => setAuthScopeVersion((version) => version + 1);
    window.addEventListener('storage', handleScopeChange);
    window.addEventListener(AUTH_SCOPE_CHANGED_EVENT, handleScopeChange);
    return () => {
      window.removeEventListener('storage', handleScopeChange);
      window.removeEventListener(AUTH_SCOPE_CHANGED_EVENT, handleScopeChange);
    };
  }, []);

  useEffect(() => {
    const token = getStoredToken();
    const accountId = getStoredAccountId();
    if (token && accountId) {
      refreshFTLData(accountId);
    }
  }, [authScopeVersion, getStoredAccountId, refreshFTLData]);

  return (
    <LoadContext.Provider
      value={{
        loads,
        ftlStats,
        costPerMileData,
        timeToAcceptanceData,
        statsStatus,
        fetchFTLLoadboardData,
        fetchFTLStats,
        refreshFTLData,
        addLoad,
      }}
    >
      {children}
    </LoadContext.Provider>
  );
};
